import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { productsAPI, ordersAPI, favoritesAPI, reportsAPI, reviewsAPI, viewsAPI } from '../api';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';
import StarRating from '../components/StarRating';
import SEO from '../components/SEO';
import ChatWidget from '../components/ChatWidget';

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');

function getSellerBadge(providerJoined, providerRating, providerTotalReviews, providerVerified) {
  const rating = parseFloat(providerRating) || 0;
  const reviews = parseInt(providerTotalReviews) || 0;
  const joined = providerJoined ? new Date(providerJoined) : null;
  const monthsOnPlatform = joined ? Math.floor((Date.now() - joined.getTime()) / (1000 * 60 * 60 * 24 * 30)) : 0;

  if (rating >= 4.5 && reviews >= 20 && monthsOnPlatform >= 6)
    return { label: 'Top Seller', color: 'text-yellow-800 bg-yellow-100 border-yellow-200', icon: '🏆', desc: 'Excellent ratings, 20+ reviews, 6+ months' };
  if (rating >= 4.0 && reviews >= 10 && monthsOnPlatform >= 3)
    return { label: 'Trusted', color: 'text-green-700 bg-green-100 border-green-200', icon: '✅', desc: 'Good ratings, 10+ reviews, 3+ months' };
  if (providerVerified)
    return { label: 'Verified', color: 'text-blue-700 bg-blue-100 border-blue-200', icon: '🔵', desc: 'Identity verified by TopInfo' };
  if (monthsOnPlatform >= 1)
    return { label: 'Active', color: 'text-gray-600 bg-gray-100 border-gray-200', icon: '🟢', desc: 'Active on the platform' };
  return { label: 'Just Joined', color: 'text-purple-700 bg-purple-100 border-purple-200', icon: '👤', desc: 'New to TopInfo' };
}

function getMemberDuration(joinDate) {
  if (!joinDate) return null;
  const joined = new Date(joinDate);
  const now = new Date();
  const days = Math.floor((now - joined) / (1000 * 60 * 60 * 24));
  if (days < 1) return 'Joined today';
  if (days < 30) return `Member for ${days} days`;
  const months = Math.floor(days / 30);
  if (months < 12) return `Member for ${months} month${months > 1 ? 's' : ''}`;
  const years = Math.floor(months / 12);
  return `Member for ${years} year${years > 1 ? 's' : ''}`;
}

const ProductDetailPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [ordering, setOrdering] = useState(false);
  const [mainImg, setMainImg] = useState(0);
    const [isFav, setIsFav] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDesc, setReportDesc] = useState('');
  const [reportSent, setReportSent] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, review_text: '' });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [viewCount, setViewCount] = useState(0);

  useEffect(() => {
    productsAPI.getById(id)
      .then((res) => setProduct(res.data.product))
      .catch(() => navigate('/products'))
      .finally(() => setLoading(false));
    // Record view and get count
    viewsAPI.record({ item_type: 'product', item_id: parseInt(id) })
      .then(res => setViewCount(res.data.views))
      .catch(() => viewsAPI.getCount('product', id).then(r => setViewCount(r.data.views)).catch(() => {}));
    reviewsAPI.getByProduct(id).then(res => {
      setReviews(res.data.reviews || []);
      setReviewStats(res.data.stats || null);
    }).catch(() => {});
    if (user) favoritesAPI.getIds().then(res => {
      setIsFav((res.data.favorites || []).some(f => f.item_type === 'product' && f.item_id === parseInt(id)));
    }).catch(() => {});
  }, [id, navigate, user]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) return navigate('/login');
    setSubmittingReview(true);
    try {
      await reviewsAPI.create({ product_id: parseInt(id), ...reviewForm });
      const res = await reviewsAPI.getByProduct(id);
      setReviews(res.data.reviews || []);
      setReviewStats(res.data.stats || null);
      setShowReviewForm(false);
      setReviewForm({ rating: 5, review_text: '' });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleOrder = async () => {
    if (!user) return navigate('/login');
    setOrdering(true);
    try {
      await ordersAPI.create({ product_id: product.id, quantity, shipping_address: '', phone: user.phone });
      alert('Order placed successfully!');
      navigate('/orders');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to place order');
    } finally {
      setOrdering(false);
    }
  };

  const toggleFav = async () => {
    if (!user) return navigate('/login');
    const res = await favoritesAPI.toggle({ item_type: 'product', item_id: parseInt(id) });
    setIsFav(res.data.action === 'added');
  };

  if (loading) return <LoadingSpinner />;
  if (!product) return null;

  const images = typeof product.images === 'string' ? JSON.parse(product.images || '[]') : (product.images || []);
  const phone = product.phone;
  const badge = getSellerBadge(product.provider_joined, product.provider_rating, product.provider_total_reviews, product.provider_verified);
  const memberDuration = getMemberDuration(product.provider_joined);
  const condition = product.condition || 'new';

  return (
    <div className="bg-gray-50/50 min-h-screen">
      <SEO title={product.product_name} description={product.description?.substring(0, 160)} keywords={`${product.product_name}, ${product.category_name}, buy Rwanda, ${product.province_name || "Kigali"}, ${product.district_name || ""}, ${product.category_name} for sale Rwanda, buy ${product.category_name} Kigali, TopInfo Rwanda`} type="product" jsonLd={product ? {
              '@context': 'https://schema.org',
              '@type': 'Product',
              name: product.product_name,
              description: product.description?.substring(0, 200),
              offers: { '@type': 'Offer', price: product.price, priceCurrency: 'RWF', availability: 'https://schema.org/InStock' },
            } : undefined} />
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
          <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          <Link to="/products" className="hover:text-primary-600 transition-colors">Products</Link>
          <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          <span className="text-gray-900 font-medium line-clamp-1">{product.product_name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Images + Details */}
          <div className="lg:col-span-2 space-y-4">
            {/* Image Gallery */}
            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
              {images.length > 0 ? (
                <>
                  <div className="relative aspect-[16/10] bg-gray-100">
                    <img src={`${API_URL}${images[mainImg]}`} alt={product.product_name} className="w-full h-full object-contain" />
                    <button onClick={toggleFav} className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full shadow flex items-center justify-center">
                      <svg className={`w-5 h-5 ${isFav ? 'text-red-500 fill-current' : 'text-gray-400'}`} fill={isFav ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </button>
                    {/* Condition badge on image */}
                    <span className={`absolute top-3 left-3 inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg font-semibold shadow-sm ${condition === 'used' ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {condition === 'used' ? '♻️ Used' : '✨ Brand New'}
                    </span>
                    {images.length > 1 && (
                      <span className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded">{mainImg + 1}/{images.length}</span>
                    )}
                  </div>
                  {images.length > 1 && (
                    <div className="flex gap-2 p-3 overflow-x-auto">
                      {images.map((img, i) => (
                        <button key={i} onClick={() => setMainImg(i)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${mainImg === i ? 'border-primary-500' : 'border-transparent'}`}>
                          <img src={`${API_URL}${img}`} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="aspect-[16/10] bg-gray-100 flex items-center justify-center text-gray-300">
                  <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">{product.product_name}</h1>
                  <div className="flex items-center gap-3 text-sm text-gray-500 flex-wrap">
                    <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs font-medium">{product.category_name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${condition === 'used' ? 'bg-orange-50 text-orange-600' : 'bg-emerald-50 text-emerald-600'}`}>
                      {condition === 'used' ? '♻️ Used item' : '✨ Brand New'}
                    </span>
                    {(product.province_name || product.location) && (
                      <span>📍 {[product.province_name, product.district_name, product.sector_name].filter(Boolean).join(' › ') || product.location}</span>
                    )}
                    <span className="flex items-center gap-1 text-gray-400">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      {viewCount} view{viewCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-600">{Number(product.price).toLocaleString()} RWF</p>
                  <p className="text-xs text-gray-400">{product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}</p>
                </div>
              </div>
              <hr className="my-4" />
              <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{product.description}</p>
            </div>

            {/* Reviews Section */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="font-semibold text-gray-900">Reviews ({reviewStats?.total_reviews || 0})</h3>
                  {reviewStats && parseInt(reviewStats.total_reviews) > 0 && (
                    <div className="flex items-center gap-1.5">
                      <StarRating rating={Math.round(parseFloat(reviewStats.avg_rating) || 0)} readonly size="sm" />
                      <span className="text-sm font-medium text-gray-600">{parseFloat(reviewStats.avg_rating || 0).toFixed(1)}</span>
                    </div>
                  )}
                </div>
                {user && user.id !== product.seller_id && !showReviewForm && (
                  <button onClick={() => setShowReviewForm(true)} className="text-sm font-semibold text-primary-600 hover:text-primary-700">
                    Write a Review
                  </button>
                )}
              </div>

              {showReviewForm && (
                <form onSubmit={handleSubmitReview} className="mb-5 bg-gray-50 rounded-xl p-4 space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Your Rating</label>
                    <StarRating rating={reviewForm.rating} onRate={(r) => setReviewForm({ ...reviewForm, rating: r })} />
                  </div>
                  <div>
                    <textarea value={reviewForm.review_text} onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                      placeholder="Share your experience with this product..." rows={3}
                      className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 outline-none" />
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" disabled={submittingReview}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition">
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </button>
                    <button type="button" onClick={() => setShowReviewForm(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
                  </div>
                </form>
              )}

              {reviews.length === 0 ? (
                <p className="text-gray-400 text-sm">No reviews yet. Be the first to review this product!</p>
              ) : (
                <div className="max-h-80 overflow-y-auto space-y-3 pr-1">
                  {reviews.map(r => (
                    <div key={r.id} className="flex gap-3 border-b border-gray-100 pb-3 last:border-0">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-600 flex-shrink-0">
                        {r.first_name?.[0]}{r.last_name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-medium text-gray-900">{r.first_name} {r.last_name}</span>
                          <StarRating rating={r.rating} readonly size="sm" />
                        </div>
                        {r.review_text && <p className="text-gray-600 text-sm">{r.review_text}</p>}
                        <p className="text-xs text-gray-400 mt-1">{new Date(r.created_at).toLocaleDateString('en-RW', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Safety Tips */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-5">
              <h3 className="font-semibold text-yellow-800 text-sm mb-2">⚠️ Safety Tips</h3>
              <ul className="text-xs text-yellow-700 space-y-1.5">
                <li className="flex items-start gap-2"><span>🛡️</span><span><strong>Choose a safe meeting spot</strong> — Pick a busy, public area when meeting the seller to view the item.</span></li>
                <li className="flex items-start gap-2"><span>💰</span><span><strong>Keep your money until you verify</strong> — Never transfer funds or pay upfront, even if delivery is promised.</span></li>
                <li className="flex items-start gap-2"><span>🔍</span><span><strong>Check everything first</strong> — Examine the product thoroughly and test it before handing over any payment.</span></li>
              </ul>
            </div>

            {/* Report Button */}
            {user && user.id !== product.seller_id && (
              <div>
                {!showReport && !reportSent && (
                  <button onClick={() => setShowReport(true)} className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1.5">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" /></svg>
                    Report this listing
                  </button>
                )}
                {reportSent && <p className="text-sm text-green-600">Report submitted. Thank you!</p>}
                {showReport && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
                    <h4 className="font-semibold text-red-800 text-sm">Report this product</h4>
                    <select value={reportReason} onChange={e => setReportReason(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
                      <option value="">Select reason...</option>
                      <option value="spam">Spam</option>
                      <option value="fraud">Fraud / Scam</option>
                      <option value="inappropriate">Inappropriate content</option>
                      <option value="fake">Fake listing</option>
                      <option value="other">Other</option>
                    </select>
                    <textarea value={reportDesc} onChange={e => setReportDesc(e.target.value)} placeholder="Additional details (optional)" rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />
                    <div className="flex gap-2">
                      <button onClick={async () => {
                        if (!reportReason) return;
                        try { await reportsAPI.create({ reported_type: 'product', reported_id: product.id, reason: reportReason, description: reportDesc }); setReportSent(true); setShowReport(false); } catch { alert('Failed to submit report'); }
                      }} disabled={!reportReason} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">Submit</button>
                      <button onClick={() => setShowReport(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-5 sticky top-24 shadow-sm">
              {/* Seller Info */}
              <Link to={`/providers/${product.seller_id}`} className="flex items-center gap-3 mb-3 hover:bg-gray-50 -mx-2 px-2 py-1.5 rounded-lg transition">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold flex-shrink-0">
                  {product.first_name?.[0]}{product.last_name?.[0]}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm">{product.first_name} {product.last_name}</p>
                  <p className="text-xs text-gray-500">{[product.province_name, product.district_name].filter(Boolean).join(' › ') || product.location || ''}</p>
                </div>
              </Link>

              {/* Seller Trust Badge */}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border mb-3 ${badge.color}`}>
                <span className="text-base">{badge.icon}</span>
                <div>
                  <p className="text-xs font-semibold">{badge.label}</p>
                  <p className="text-[10px] opacity-75">{badge.desc}</p>
                </div>
              </div>
              {memberDuration && (
                <p className="text-[11px] text-gray-400 mb-3 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  {memberDuration}
                </p>
              )}

              <Link to={`/providers/${product.seller_id}`}
                className="w-full flex items-center justify-center gap-2 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition text-sm mb-3">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                View Seller Profile
              </Link>

              {/* Contact Buttons */}
              {phone && (
                <div className="mb-3 space-y-2">
                  <a href={`tel:${phone}`} className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white py-3 rounded-xl font-semibold hover:bg-primary-700 transition">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                    Call {phone}
                  </a>
                  <a href={`https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi, I'm interested in: ${product.product_name} (${Number(product.price).toLocaleString()} RWF) on TopInfo.`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 bg-green-500 text-white py-3 rounded-xl font-semibold hover:bg-green-600 transition">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </a>
                </div>
              )}

              {/* Chat */}
              <ChatWidget sellerId={product.seller_id} sellerName={`${product.first_name} ${product.last_name}`} itemTitle={product.product_name} />

              {/* Order */}
              {product.stock > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium">Quantity:</span>
                    <div className="flex items-center border rounded-lg">
                      <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-3 py-1.5 hover:bg-gray-100 text-sm">-</button>
                      <span className="px-3 py-1.5 border-x text-sm">{quantity}</span>
                      <button onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} className="px-3 py-1.5 hover:bg-gray-100 text-sm">+</button>
                    </div>
                  </div>
                  <p className="text-sm font-semibold mb-2">Total: {(product.price * quantity).toLocaleString()} RWF</p>
                  <button onClick={handleOrder} disabled={ordering}
                    className="w-full bg-primary-600 text-white py-2.5 rounded-lg font-bold hover:brightness-95 disabled:opacity-50 transition text-sm">
                    {ordering ? 'Placing...' : 'Order Now'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
